<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  columns: { type: Array, default: () => [] },
  isDealsMode: { type: Boolean, default: false },
  funnelId: { type: [String, Number], default: null },
  // Aberto pela aba "Negócio" da conversa: o contato já é conhecido e não há
  // funil ativo, então o funil vira uma escolha aqui dentro.
  presetContact: { type: Object, default: null },
});
const emit = defineEmits(['close', 'negotiationAdded']);

const store = useStore();

const contactQuery = ref('');
const searchResults = ref([]);
const selectedContact = ref(null);
const isSearching = ref(false);
const selectedColumnLabel = ref('');
const selectedStageId = ref(null);
const dealTitle = ref('');
const selectedAssigneeId = ref(null);
const dealValue = ref('');
const isSaving = ref(false);
const errorMessage = ref('');
let searchTimer = null;

const agents = computed(() => store.getters['agents/getAgents']);
const currentUser = computed(() => store.getters.getCurrentUser);

// Fora do quadro não existe funil ativo: `funnelId` vem nulo e o funil passa a
// ser escolhido aqui, com as etapas saindo dele em vez das colunas da tela.
const funnels = computed(() => store.getters['crmDeals/getFunnels'] || []);
const pickedFunnelId = ref(null);
const needsFunnelPicker = computed(() => !props.funnelId);
const effectiveFunnelId = computed(
  () => props.funnelId || pickedFunnelId.value
);
const effectiveColumns = computed(() => {
  if (!needsFunnelPicker.value) return props.columns;
  const funnel = funnels.value.find(
    f => String(f.id) === String(effectiveFunnelId.value)
  );
  return (funnel?.stages || []).map(s => ({
    id: s.id,
    stage_id: s.id,
    name: s.name,
    label: s.name,
  }));
});
// A negociação nasce numa inbox de canal API dedicada ("CRM – Negociações")
// pra nunca receber mensagens de WhatsApp e não fragmentar o chat principal.
// A inbox escolhida fica salva na conta (crm_negotiation_inbox_id) — clientes
// que já usam canal API pra integrações (ex.: Uazapi) escolhem a dedicada.
const selectedInboxId = ref('');
const apiInboxes = computed(() =>
  (store.getters['inboxes/getInboxes'] || []).filter(
    i => i.channel_type === INBOX_TYPES.API
  )
);
const currentAccount = computed(() =>
  store.getters['accounts/getAccount'](store.getters.getCurrentAccountId)
);
const savedNegotiationInboxId = computed(() => {
  const id = currentAccount.value?.custom_attributes?.crm_negotiation_inbox_id;
  return id ? Number(id) : null;
});
const isFormValid = computed(() => {
  if (props.isDealsMode) {
    return !!selectedContact.value && !!selectedStageId.value;
  }
  return (
    !!selectedContact.value &&
    !!selectedColumnLabel.value &&
    !!selectedInboxId.value
  );
});

const resetForm = () => {
  searchResults.value = [];
  selectedContact.value = props.presetContact || null;
  contactQuery.value = props.presetContact?.name || '';
  dealValue.value = '';
  dealTitle.value = '';
  errorMessage.value = '';
  isSaving.value = false;
  selectedAssigneeId.value = currentUser.value?.id || null;

  if (needsFunnelPicker.value) {
    if (!funnels.value.length) store.dispatch('crmDeals/fetchFunnels');
    pickedFunnelId.value = funnels.value[0]?.id || null;
  }

  if (props.isDealsMode) {
    selectedStageId.value = effectiveColumns.value.length
      ? effectiveColumns.value[0].stage_id
      : null;
    return;
  }

  selectedColumnLabel.value = props.columns.length
    ? props.columns[0].label
    : '';
  // Só pré-seleciona a inbox salva na conta. Nunca auto-seleciona outra inbox
  // API: ela pode ser uma integração real (ex.: Uazapi) e as conversas dela
  // seriam escondidas da lista de chats.
  const saved = savedNegotiationInboxId.value;
  const savedIsApi = apiInboxes.value.some(i => i.id === saved);
  selectedInboxId.value = savedIsApi ? saved : '';
};

watch(
  () => props.show,
  val => {
    if (val) resetForm();
  }
);

// Trocar de funil invalida a etapa escolhida: ela é de outro funil, e o modelo
// recusa a combinação. Cai na primeira etapa do funil novo.
watch(pickedFunnelId, () => {
  if (!props.isDealsMode) return;
  selectedStageId.value = effectiveColumns.value[0]?.stage_id || null;
});

watch(contactQuery, query => {
  clearTimeout(searchTimer);
  if (selectedContact.value && query === selectedContact.value.name) return;
  selectedContact.value = null;
  if (!query || query.trim().length < 2) {
    searchResults.value = [];
    return;
  }
  searchTimer = setTimeout(async () => {
    isSearching.value = true;
    try {
      const { data } = await ContactAPI.search(
        encodeURIComponent(query.trim())
      );
      searchResults.value = data.payload || [];
    } catch {
      searchResults.value = [];
    } finally {
      isSearching.value = false;
    }
  }, 300);
});

const selectContact = contact => {
  selectedContact.value = contact;
  contactQuery.value = contact.name;
  searchResults.value = [];
};

const onClose = () => emit('close');

// Modo `deals`: cria um Crm::Deal de verdade. Sem inbox, sem conversa — a
// gambiarra da inbox de canal API dedicada só existe no modo conversacional.
const saveDeal = async () => {
  const payload = {
    contact_id: selectedContact.value.id,
    funnel_id: effectiveFunnelId.value,
    stage_id: selectedStageId.value,
    title: dealTitle.value.trim(),
  };
  if (dealValue.value) payload.value = dealValue.value;
  if (selectedAssigneeId.value) payload.assignee_id = selectedAssigneeId.value;

  await store.dispatch('crmDeals/createDeal', payload);

  useAlert('Negociação criada com sucesso!');
  emit('negotiationAdded');
  onClose();
};

const saveConversation = async () => {
  const payload = {
    inbox_id: selectedInboxId.value,
    contact_id: selectedContact.value.id,
  };
  if (selectedAssigneeId.value) {
    payload.assignee_id = selectedAssigneeId.value;
  }
  const convResponse = await ConversationApi.create(payload);
  const conversationId = convResponse.data.id;

  // Persiste a inbox escolhida como a inbox de negociações da conta
  // (usada pelo atalho de conversa principal e pelo filtro do chat).
  if (savedNegotiationInboxId.value !== Number(selectedInboxId.value)) {
    store.dispatch('accounts/update', {
      custom_attributes: {
        crm_negotiation_inbox_id: Number(selectedInboxId.value),
      },
    });
  }

  await store.dispatch('conversationLabels/update', {
    conversationId,
    labels: [selectedColumnLabel.value],
  });

  if (dealValue.value) {
    await ConversationApi.updateCustomAttributes({
      conversationId,
      customAttributes: { deal_value: dealValue.value },
    });
  }

  useAlert('Negociação criada com sucesso!');
  emit('negotiationAdded');
  onClose();
};

const saveNegotiation = async () => {
  isSaving.value = true;
  errorMessage.value = '';
  try {
    if (props.isDealsMode) {
      await saveDeal();
    } else {
      await saveConversation();
    }
  } catch (err) {
    useAlert('Erro ao criar a negociação. Tente novamente.');
    errorMessage.value = err.message || 'Erro de comunicação com o servidor.';
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col h-full bg-white dark:bg-slate-900">
      <div
        class="px-6 py-4 border-b border-n-weak flex justify-between items-center"
      >
        <h2
          class="text-lg font-semibold m-0 text-slate-800 dark:text-slate-100"
        >
          Adicionar Negociação
        </h2>
      </div>

      <div class="flex-1 px-6 py-4 space-y-4">
        <template v-if="!isDealsMode">
          <!-- Aviso: precisa de inbox API -->
          <div
            v-if="!apiInboxes.length"
            class="p-3 rounded-md bg-amber-500/10 text-amber-600 text-sm"
          >
            Nenhuma inbox de canal API encontrada. Crie uma inbox do tipo API
            dedicada (ex.: "CRM – Negociações") em Configurações → Caixas de
            Entrada para usar negociações.
          </div>

          <!-- Inbox das negociações -->
          <div v-else>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              Inbox das Negociações <span class="text-red-500">*</span>
            </label>
            <select
              v-model="selectedInboxId"
              class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
            >
              <option value="" disabled>Selecione a inbox dedicada...</option>
              <option
                v-for="inbox in apiInboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
            <p class="text-xs text-n-slate-10 mt-1 m-0">
              Use uma inbox API dedicada só pra negociações — as conversas dela
              somem da lista de chats. Não escolha uma inbox de integração em
              uso (ex.: Uazapi).
            </p>
          </div>
        </template>

        <!-- Título do negócio (modo deals) -->
        <div v-if="isDealsMode">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Título do Negócio
          </label>
          <input
            v-model="dealTitle"
            type="text"
            class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
            placeholder="Ex: Plano anual, Consultoria..."
          />
        </div>

        <!-- Busca de contato existente -->
        <div class="relative">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Contato existente <span class="text-red-500">*</span>
          </label>
          <input
            v-model="contactQuery"
            type="text"
            class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
            placeholder="Busque por nome ou telefone..."
          />
          <div
            v-if="searchResults.length"
            class="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-n-surface-1 border border-n-weak rounded-md shadow-lg"
          >
            <button
              v-for="contact in searchResults"
              :key="contact.id"
              class="w-full text-left px-3 py-2 hover:bg-n-alpha-1 border-none bg-transparent cursor-pointer"
              @click="selectContact(contact)"
            >
              <span class="block text-sm font-medium text-n-slate-12">{{
                contact.name
              }}</span>
              <span class="block text-xs text-n-slate-10">{{
                contact.phone_number
              }}</span>
            </button>
          </div>
          <p v-if="isSearching" class="text-xs text-n-slate-10 mt-1 m-0">
            Buscando...
          </p>
          <p v-if="selectedContact" class="text-xs text-emerald-500 mt-1 m-0">
            ✓ {{ selectedContact.name }} selecionado
          </p>
        </div>

        <!-- Funil: só quando o modal é aberto fora do quadro, onde não há
             funil ativo pra herdar. -->
        <div v-if="needsFunnelPicker && isDealsMode">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Funil <span class="text-red-500">*</span>
          </label>
          <select
            v-model="pickedFunnelId"
            class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
          >
            <option v-for="f in funnels" :key="f.id" :value="f.id">
              {{ f.name }}
            </option>
          </select>
        </div>

        <!-- Etapa do funil -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Etapa do Funil <span class="text-red-500">*</span>
          </label>
          <select
            v-if="isDealsMode"
            v-model="selectedStageId"
            class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
          >
            <option :value="null" disabled>Selecione a fase...</option>
            <option
              v-for="col in effectiveColumns"
              :key="col.stage_id"
              :value="col.stage_id"
            >
              {{ col.name }}
            </option>
          </select>
          <select
            v-else
            v-model="selectedColumnLabel"
            class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
          >
            <option value="" disabled>Selecione a fase...</option>
            <option v-for="col in columns" :key="col.id" :value="col.label">
              {{ col.label }}
            </option>
          </select>
        </div>

        <!-- Responsável -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Atribuir Para
          </label>
          <select
            v-model="selectedAssigneeId"
            class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
          >
            <option :value="null">Ninguém (Não atribuído)</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>
        </div>

        <!-- Valor do negócio (opcional) -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Valor do Negócio (R$)
          </label>
          <input
            v-model="dealValue"
            type="number"
            step="0.01"
            min="0"
            placeholder="0,00"
            class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
          />
        </div>

        <p v-if="errorMessage" class="text-red-500 text-sm mt-2 font-medium">
          {{ errorMessage }}
        </p>
      </div>

      <div
        class="px-6 py-4 border-t border-n-weak flex justify-end items-center gap-2 bg-n-alpha-1"
      >
        <NextButton
          label="Cancelar"
          variant="faded"
          color="slate"
          @click="onClose"
        />
        <NextButton
          label="Criar Negociação"
          icon="i-lucide-handshake"
          :disabled="!isFormValid || isSaving"
          :is-loading="isSaving"
          @click="saveNegotiation"
        />
      </div>
    </div>
  </woot-modal>
</template>
